import { isEqual } from 'ohash'
import type {
  Citation,
  DesignMeta,
  NodePosition,
  PolicyDocView,
} from '@worker/policy-builder/client'
import type { DecisionTree, NodeMove, TreeDirection } from './collabTree'
import { heldPlaces, layoutKey, strayLayoutKeys } from './collabTree'

/** The slice of Loro's `LoroMovableList` this file writes through (admin has no direct loro-crdt dependency). */
export interface EntryList {
  toArray(): unknown[]
  get(index: number): unknown
  set(index: number, value: unknown): void
  push(value: unknown): void
  delete(index: number, length: number): void
  clear(): void
}

/** The slice of Loro's `LoroMap` this file writes through. */
export interface KeyedMap {
  get(key: string): unknown
  set(key: string, value: unknown): void
  delete(key: string): void
}

export const DESIGN_LISTS = [
  'nodes',
  'insights',
  'metadata',
  'datasets',
  'externalDocs',
] as const

export type DesignListName = (typeof DESIGN_LISTS)[number]

/* Lists whose entries carry an id, so a write can touch only the entries it changed. */
export const ID_LISTS: readonly string[] = ['nodes', 'insights']

export type DesignPatch = Partial<
  Pick<PolicyDocView, DesignListName> & {
    designMeta: Partial<DesignMeta>
  }
>

/**
 * Rewrites `list` to `next` entry by entry — deleting what went, setting what
 * changed, appending what is new — so a peer editing a different entry at the
 * same moment merges instead of being overwritten.
 */
export const applyById = (list: EntryList, next: { id: string }[]): void => {
  const keep = new Set(next.map(({ id }) => id))
  const current = list.toArray() as { id: string }[]
  for (let index = current.length - 1; index >= 0; index--)
    if (!keep.has(current[index]!.id)) list.delete(index, 1)
  const at = new Map(
    (list.toArray() as { id: string }[]).map(({ id }, index) => [id, index]),
  )
  for (const entry of next) {
    const index = at.get(entry.id)
    if (index === undefined) list.push(entry)
    else if (!isEqual(list.get(index), entry)) list.set(index, entry)
  }
}

/** Replaces the entry with `id` in a movable list, or appends it. */
export const upsertById = (list: EntryList, entry: { id: string }): void => {
  const current = list.toArray() as { id: string }[]
  const index = current.findIndex((item) => item.id === entry.id)
  if (index === -1) list.push(entry)
  else list.set(index, entry)
}

export const removeById = (list: EntryList, id: string): void => {
  const current = list.toArray() as { id: string }[]
  const index = current.findIndex((item) => item.id === id)
  if (index !== -1) list.delete(index, 1)
}

/** Replaces one field's citations wholesale, leaving every other field's alone. */
export const replaceFieldCitations = (
  list: EntryList,
  fieldId: string,
  citations: readonly Citation[],
): void => {
  const current = list.toArray() as Citation[]
  for (let index = current.length - 1; index >= 0; index--)
    if (current[index]?.fieldId === fieldId) list.delete(index, 1)
  for (const citation of citations) list.push(citation)
}

/** Writes where nodes sit in one layout direction. */
export const writePositions = (
  layout: KeyedMap,
  direction: TreeDirection,
  moves: readonly NodeMove[],
): void => {
  for (const { id, position } of moves)
    layout.set(layoutKey(direction, id), {
      x: Math.round(position.x),
      y: Math.round(position.y),
    })
}

/** Forgets every dragged position in one direction, back to the computed layout. */
export const clearDirection = (
  layout: KeyedMap,
  keys: readonly string[],
  direction: TreeDirection,
): void => {
  for (const key of keys)
    if (key.startsWith(`${direction}:`)) layout.delete(key)
}

/** Writes whole design lists and meta fields, touching only those that differ. */
export const applyDesignPatch = (
  lists: Record<DesignListName, EntryList>,
  meta: KeyedMap,
  patch: DesignPatch,
): void => {
  for (const name of DESIGN_LISTS) {
    const next = patch[name]
    const list = lists[name]
    if (!next || isEqual(list.toArray(), next)) continue
    if (ID_LISTS.includes(name)) {
      applyById(list, next as { id: string }[])
      continue
    }
    list.clear()
    for (const entry of next) list.push(entry)
  }
  for (const [key, value] of Object.entries(patch.designMeta ?? {}))
    if (meta.get(key) !== value) meta.set(key, value)
}

/** One edit to the decision tree, written in one commit with where any `placed` nodes sit. */
export const applyTreeEdit = (
  containers: { nodes: EntryList; insights: EntryList; layout: KeyedMap },
  before: DecisionTree,
  next: DecisionTree,
  layout: Record<string, NodePosition>,
  placed?: { direction: TreeDirection; moves: NodeMove[] },
): void => {
  applyById(containers.nodes, next.nodes)
  applyById(containers.insights, next.insights)
  for (const key of strayLayoutKeys(layout, next))
    containers.layout.delete(key)
  for (const direction of ['LR', 'TB'] as const)
    writePositions(
      containers.layout,
      direction,
      heldPlaces(before, next, layout, direction),
    )
  if (placed) writePositions(containers.layout, placed.direction, placed.moves)
}



export const patchMap = (
  map: KeyedMap,
  patch: Record<string, unknown>,
): void => {
  for (const [key, value] of Object.entries(patch))
    if (!isEqual(map.get(key), value)) map.set(key, value)
}

export const decisionCitation = (
  fieldId: string,
  body: string,
  by: string,
  at: string,
  id: string,
): Citation => ({
  id,
  fieldId,
  kind: 'decision',
  text: body,
  cite: '',
  referenceDocumentId: null,
  excerptId: null,
  anchor: null,
  by,
  at,
})
